import React, { useState, useContext, useEffect } from 'react';
import { Routes, Route, NavLink as RouterNavLink, Navigate, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { Card, CardHeader, CardBody, CardTitle, Row, Col, Nav, NavItem, NavLink, TabContent, TabPane, FormGroup, Button } from 'reactstrap';
import toast from 'react-hot-toast';

import { SystemPreferencesSection } from './partials/general-settings/SystemPreferencesSection';
import { DataFieldSection } from './partials/general-settings/DataFieldSection';
import { CustomizationsSection } from './partials/general-settings/CustomizationsSection';

import { setting } from '../../api';
import UserContext from '../UserContext';
import { FaSpinner } from 'react-icons/fa';
import { SettingsGeneralDB } from 'api/settings/general';
import { ForbiddenException } from 'api/exceptions/ForbiddenException';
import { FailedToFetchException } from 'api/exceptions/FailedToFetchException';

export const GeneralSettings = () => {
	const { hash: locationHash, pathname: locationPathname, search: locationSearch } = useLocation();
	const { jwt } = useContext(UserContext);
	const [data, setData] = useState<SettingsGeneralDB>({ data_fields: [], site_name: '' });
	const [isLoaded, setIsLoaded] = useState(false);
	const [formSaveInProgress, setFormSaveInProgress] = useState(false);
	const [hasFormChanged, setHasFormChanged] = useState(false);

	/**
	 * This function will retrieve the settings data from the backend
	 */
	useEffect(() => {
		const getContents = async () => {
			try {
				const response = await setting.general.get(jwt);

				setData(response);
				setIsLoaded(true);
			} catch (error) {
				if (error instanceof ForbiddenException) {
					toast.error(error.message);
				} else if (error instanceof FailedToFetchException) {
					toast.error(error.message);
				} else {
					console.error('error');
					console.error(error);
					Sentry.captureException(error);
					toast.error('Failed to load');
				}
			}
		};

		getContents();
	}, []);

	/**
	 * This function handles changing any of the fields
	 * @param fieldName the name of the field to be updated
	 * @param value the new value to be saved
	 */
	const handleFieldChange = (fieldName: keyof SettingsGeneralDB, value: any) => {
		const newData = { ...data };
		newData[fieldName] = value;

		setData(newData);
		setHasFormChanged(true);
	};

	/**
	 * This function will save settings to the backend
	 */
	const handleSettingsSave = () => {
		Promise.resolve()
			.then(() => {
				setFormSaveInProgress(true);
			})
			.then(() => {
				return setting.general.update(jwt, data).then((result) => {
					setHasFormChanged(false);
					setFormSaveInProgress(false);
					toast.success('Successfully saved settings');
				});
			});
	};

	/**
	 * The render function for the React component
	 */
	return (
		<div className="content general-settings">
			<Row>
				<Col md="12">
					<Card>
						<CardHeader>
							<CardTitle tag="h4">General Settings</CardTitle>
						</CardHeader>
						<CardBody>
							{isLoaded ? (
								<>
									<div className="nav-tabs-navigation">
										<div className="nav-tabs-wrapper">
											<Nav id="tabs" role="tablist" tabs>
												<NavItem key={`system-preference-nav-item`}>
													<NavLink tag={RouterNavLink} to={`system-preference`} end>
														System Preferences
													</NavLink>
												</NavItem>
												<NavItem key={`data-field-nav-item`}>
													<NavLink tag={RouterNavLink} to={`data-field`} end>
														Data Fields
													</NavLink>
												</NavItem>
												<NavItem key={`customizations-nav-item`}>
													<NavLink tag={RouterNavLink} to={`customizations`} end>
														Customizations
													</NavLink>
												</NavItem>
											</Nav>
										</div>
									</div>

									<TabContent>
										<TabPane>
											<Routes>
												<Route
													path={`system-preference`}
													element={<SystemPreferencesSection data={data} onChange={handleFieldChange} />}
												/>
												<Route
													path={`data-field/:action?`}
													element={<DataFieldSection data={data} onChange={(newData) => handleFieldChange('data_fields', newData)} />}
												/>
												<Route path={`customizations`} element={<CustomizationsSection data={data} onChange={handleFieldChange} />} />
												<Route index element={<Navigate to={`system-preference`} />} />
											</Routes>
										</TabPane>
									</TabContent>
									<hr />
									<FormGroup check row>
										<Col sm={{ size: 10, offset: 2 }}>
											<Button color="primary" onClick={() => handleSettingsSave()} disabled={!hasFormChanged || formSaveInProgress}>
												{formSaveInProgress ? <FaSpinner size={12} className="fa-spin" /> : 'Save'}
											</Button>
										</Col>
									</FormGroup>
								</>
							) : (
								<FaSpinner size={12} color="#ccc" className="fa-spin" />
							)}
						</CardBody>
					</Card>
				</Col>
			</Row>
		</div>
	);
};
